/**
 * ParticipantsWidget — View teams/players for a sport
 * Extracted from: fetchParticipants() lines 2532-2576
 * API: GET /participants/:sport (1 token)
 * CSS prefix: ptw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchOddsSports, fetchOddsParticipants } from '../lib/api'
import { getSportIcon } from '../lib/oddsConstants'
import { Users, RefreshCw, ChevronRight, Search } from 'lucide-react'
import './ParticipantsWidget.css'

function ParticipantsWidget({ onQuotaUpdate }) {
  const [sports, setSports] = useState([])
  const [selectedSport, setSelectedSport] = useState('')
  const [participants, setParticipants] = useState([])
  const [loading, setLoading] = useState(false)
  const [sportsLoading, setSportsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [lastFetch, setLastFetch] = useState(null)

  // Load sports list for dropdown
  useEffect(() => {
    async function loadSports() {
      setSportsLoading(true)
      const res = await fetchOddsSports()
      if (res.status === 'ok' && res.data) {
        setSports(res.data.filter(s => s.active && !s.has_outrights))
      }
      setSportsLoading(false)
    }
    loadSports()
  }, [])

  const fetchData = useCallback(async () => {
    if (!selectedSport) return
    setLoading(true)
    setError(null)

    const res = await fetchOddsParticipants(selectedSport)

    if (res.status === 'ok' && res.data) {
      setParticipants(res.data)
      setLastFetch(new Date())
      if (onQuotaUpdate && (res.remaining || res.used)) onQuotaUpdate(res.remaining, res.used)
    } else {
      setError(res.message || 'Failed to load participants')
      setParticipants([])
    }

    setLoading(false)
  }, [selectedSport])

  const filtered = participants.filter(p => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      p.full_name?.toLowerCase().includes(term) ||
      p.id?.toLowerCase().includes(term)
    )
  })

  return (
    <div className="widget-card">
      <div
        className="widget-header ptw-header-clickable"
        onClick={() => setExpanded(e => !e)}
      >
        <Users size={16} className="widget-icon" />
        <span className="widget-title">Participants</span>
        {participants.length > 0 && (
          <span className="ptw-count">{participants.length} teams/players</span>
        )}
        <span className="status-badge warning" style={{ fontSize: 9 }}>1 TOKEN</span>
        <ChevronRight
          size={16}
          className={`ptw-chevron ${expanded ? 'rotated' : ''}`}
        />
      </div>

      {expanded && (
        <>
          {/* Controls */}
          <div className="ptw-controls">
            <select
              className="ptw-select"
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
              disabled={sportsLoading}
            >
              <option value="">
                {sportsLoading ? 'Loading sports...' : 'Select a sport'}
              </option>
              {sports.map(s => (
                <option key={s.key} value={s.key}>
                  {getSportIcon(s.key)} {s.title}
                </option>
              ))}
            </select>

            <button
              className="ptw-fetch-btn"
              onClick={fetchData}
              disabled={!selectedSport || loading}
            >
              {loading ? <RefreshCw size={13} className="spinning" /> : <RefreshCw size={13} />}
              <span>Get Participants</span>
            </button>
          </div>

          {/* Search (only when we have results) */}
          {participants.length > 0 && (
            <div className="ptw-search-bar">
              <Search size={14} />
              <input
                type="text"
                placeholder="Search participants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ptw-search-input"
              />
              <span className="ptw-match-count">
                {filtered.length} of {participants.length}
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="widget-body">
              <div className="widget-error">{error}</div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="widget-body">
              <div className="widget-empty">Loading participants...</div>
            </div>
          )}

          {/* Results grid */}
          {!loading && filtered.length > 0 && (
            <div className="ptw-grid">
              {filtered.map(p => (
                <div key={p.id} className="ptw-card">
                  <div className="ptw-name">{p.full_name}</div>
                  <div className="ptw-id">ID: {p.id}</div>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && participants.length === 0 && selectedSport && lastFetch && (
            <div className="widget-body">
              <div className="widget-empty">No participants found for this sport.</div>
            </div>
          )}

          {/* No sport selected */}
          {!loading && !selectedSport && (
            <div className="widget-body">
              <div className="widget-empty">Select a sport to view its teams and players.</div>
            </div>
          )}

          {/* Footer */}
          <div className="widget-footer">
            <span>{lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : 'Not loaded'}</span>
            <span>{selectedSport && `Sport: ${selectedSport}`}</span>
          </div>
        </>
      )}
    </div>
  )
}

export default ParticipantsWidget
